import React from "react";
import { FileCardOptionsTypeEnum } from "app/components/MediaUploader/types";
import { useAttachmentPreviewNavigation } from "app/containers/Features/containers/Ticket/components/AttachmentPreview/hooks/useAttachmentPreviewNavigation";
import type { TicketAttachmentFile } from "app/containers/Features/containers/Ticket/types";

type UseAttachmentPreviewControllerParams = {
  files: TicketAttachmentFile[];
  previewFileId: string | null;
  previewFileIds: string[] | null;
  isPreviewOpen: boolean;
  onPreviewFileChange: (fileId: string | null) => void;
  onOpenPreview?: (file: TicketAttachmentFile) => void;
  onPreviewOpenChange: (open: boolean) => void;
  onAttachmentAction: (
    type: FileCardOptionsTypeEnum,
    file: TicketAttachmentFile,
  ) => void | Promise<void>;
  onRenameSubmit?: (file: TicketAttachmentFile, nextName: string) => void;
  onDeleteBeforeAction?: () => void;
};

export const useAttachmentPreviewController = ({
  files,
  previewFileId,
  previewFileIds,
  isPreviewOpen,
  onPreviewFileChange,
  onOpenPreview,
  onPreviewOpenChange,
  onAttachmentAction,
  onRenameSubmit,
  onDeleteBeforeAction,
}: UseAttachmentPreviewControllerParams) => {
  const previewFile = React.useMemo(
    () => files.find((file) => file.id === previewFileId) ?? null,
    [files, previewFileId],
  );

  const previewNavigationIds = React.useMemo(() => {
    if (!previewFileIds?.length) return files.map((file) => file.id);
    const fileIdSet = new Set(files.map((file) => file.id));
    return previewFileIds.filter((id) => fileIdSet.has(id));
  }, [files, previewFileIds]);

  const { canGoPrevious, canGoNext, goPrevious, goNext, activeIndex } =
    useAttachmentPreviewNavigation({
      itemIds: previewNavigationIds,
      activeItemId: previewFileId,
      onActiveItemChange: (id) => {
        onPreviewFileChange(id);
      },
    });

  const handlePreview = React.useCallback(
    (file: TicketAttachmentFile) => {
      if (onOpenPreview) {
        onOpenPreview(file);
        return;
      }
      onPreviewFileChange(file.id);
      onPreviewOpenChange(true);
    },
    [onOpenPreview, onPreviewFileChange, onPreviewOpenChange],
  );

  const closePreview = React.useCallback(() => {
    onPreviewOpenChange(false);
    onPreviewFileChange(null);
  }, [onPreviewFileChange, onPreviewOpenChange]);

  const handlePreviewDownload = React.useCallback(() => {
    if (!previewFile) return;
    void onAttachmentAction(FileCardOptionsTypeEnum.DOWNLOAD, previewFile);
  }, [onAttachmentAction, previewFile]);

  const handlePreviewOpenFile = React.useCallback(() => {
    if (!previewFile?.url) return;
    window.open(previewFile.url, "_blank", "noopener,noreferrer");
  }, [previewFile]);

  const handlePreviewCopyUrl = React.useCallback(() => {
    if (!previewFile) return;
    void onAttachmentAction(FileCardOptionsTypeEnum.COPY_URL, previewFile);
  }, [onAttachmentAction, previewFile]);

  const handlePreviewDelete = React.useCallback(() => {
    if (!previewFile) return;
    onDeleteBeforeAction?.();
    void onAttachmentAction(FileCardOptionsTypeEnum.IS_REMOVE, previewFile);
  }, [onAttachmentAction, onDeleteBeforeAction, previewFile]);

  const handlePreviewRenameSubmit = React.useCallback(
    (nextName: string) => {
      if (!previewFile || !onRenameSubmit) return;
      onRenameSubmit(previewFile, nextName);
    },
    [onRenameSubmit, previewFile],
  );

  return {
    isPreviewOpen,
    previewFile,
    handlePreview,
    closePreview,
    handlePreviewDownload,
    handlePreviewOpenFile,
    handlePreviewCopyUrl,
    handlePreviewDelete,
    handlePreviewRenameSubmit,
    onPrevious: canGoPrevious ? goPrevious : undefined,
    onNext: canGoNext ? goNext : undefined,
    currentItemNumber: activeIndex >= 0 ? activeIndex + 1 : 1,
    totalItems: previewNavigationIds.length || 1,
  };
};

export default useAttachmentPreviewController;
